/**
 * Pure geometry and policy for the clause screen grab.
 *
 * Kept out of `library.ts` so the unit tests can import it without pulling the
 * whole screen module in behind it: `library.ts` reaches `src/assets.ts`, whose
 * image imports are typed by `vite/client`, and the tests TypeScript project
 * deliberately declares only the Workers types. Nothing in here touches the
 * document — it works on plain sizes, so a size that had to be recovered by
 * decoding the bytes is treated exactly like one the browser already knew.
 */

/** Intrinsic size of one asset segment, however it was obtained. */
export type ImageSize = { width: number; height: number };

/** Canvas dimensions browsers will refuse beyond, so a pane is scaled to stay inside it. */
export const MAX_CANVAS_EDGE = 8192;

/** Gap the stylesheet leaves between stacked segments of one logical asset. */
export const ASSET_SEGMENT_GAP = 4;

/**
 * The largest scale at which every segment of an asset fits, stacked, inside
 * the space given. Never enlarges past natural size — design §3.6.
 */
export function fitScaleFor(sizes: ImageSize[], availableWidth: number, availableHeight: number): number {
  const naturalWidth = Math.max(...sizes.map((size) => size.width));
  const naturalHeight = sizes.reduce((total, size) => total + size.height, 0);
  const gaps = Math.max(0, sizes.length - 1) * ASSET_SEGMENT_GAP;
  return Math.min(1, availableWidth / naturalWidth, Math.max(1, availableHeight - gaps) / naturalHeight);
}

/**
 * Capture fits to width alone. On screen the viewport is height-capped so a
 * tall asset stays scrollable inside the page, and `fitScaleFor` takes the
 * smaller of the two fits — which drove Table 3.2 down to 18% of its size, with
 * most of the image width left empty. A still image does not scroll, so the
 * height cap has nothing to buy and the asset should simply fill the width.
 */
export function captureFitScale(sizes: ImageSize[], availableWidth: number): number {
  return fitScaleFor(sizes, availableWidth, Number.POSITIVE_INFINITY);
}

/** Height the stacked, scaled segments occupy, including the gaps between them. */
export function fittedStackHeight(sizes: ImageSize[], scale: number): number {
  return sizes.reduce((total, size) => total + size.height * scale, 0)
    + Math.max(0, sizes.length - 1) * ASSET_SEGMENT_GAP;
}

/**
 * App furniture that must not appear in a shared image. The action row is the
 * obvious one. The zoom controls matter for a subtler reason: capture re-fits
 * the asset, so their read-out would state a percentage that is no longer true
 * — leaving them in would put wrong information in the picture.
 */
export function captureHiddenSelectors(): readonly string[] {
  return [".clause-actions", ".asset-viewer-toolbar"];
}

/**
 * Held down so a single pane cannot exceed what a browser will allocate. This
 * matters more since capture stopped height-capping assets: a tall table now
 * renders at full width and can be several thousand pixels tall before the
 * device pixel ratio is applied.
 */
export function cappedCaptureScale(scale: number, width: number, height: number): number {
  // No lower floor. A floor would win against the ceiling on a pane taller than
  // 81,920 CSS pixels and hand back a scale that allocates an oversized canvas —
  // which browsers answer with a blank image rather than an error. The divisor
  // is guarded instead, so the result is always above zero.
  return Math.min(scale, MAX_CANVAS_EDGE / Math.max(1, width, height));
}
